/**
 * When a done card's task was finished, short enough to stay on the card's one line (docs/ui-audit.md
 * 2026-10-02, item 13): "Today 3:42 PM", "Yesterday 3:42 PM", then the day alone, "Sep 22", with the
 * year when it is not this year, "Sep 22, 2025". Days are calendar days where the reader is (or in
 * `timeZone`, which the tests pass so they read the same anywhere). Both clients' done cards use it.
 */

const DAY_MS = 86_400_000;

// One formatter per shape and place, made on first use: building an Intl.DateTimeFormat costs far
// more than formatting with one (client/lib/format.ts has the numbers).
const formats = new Map<string, Intl.DateTimeFormat>();

function formatter(kind: "day" | "time" | "short" | "long", locale: string | undefined, timeZone: string | undefined): Intl.DateTimeFormat {
  const key = `${kind}|${locale ?? ""}|${timeZone ?? ""}`;
  let f = formats.get(key);
  if (!f) {
    const options: Intl.DateTimeFormatOptions =
      kind === "day"
        ? { year: "numeric", month: "numeric", day: "numeric" }
        : kind === "time"
          ? { hour: "numeric", minute: "2-digit" }
          : kind === "short"
            ? { month: "short", day: "numeric" }
            : { month: "short", day: "numeric", year: "numeric" };
    f = new Intl.DateTimeFormat(locale, { ...options, timeZone });
    formats.set(key, f);
  }
  return f;
}

/** The calendar day a moment falls on where the reader is, as a day count, and its year. Read in
 * en-US whatever the reader's language, so the parts are always Western digits. */
function calendarDay(ms: number, timeZone: string | undefined): { day: number; year: number } {
  const parts = formatter("day", "en-US", timeZone).formatToParts(new Date(ms));
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const year = part("year");
  return { day: Date.UTC(year, part("month") - 1, part("day")) / DAY_MS, year };
}

export function formatDoneAt(iso: string | null | undefined, now = Date.now(), locale?: string, timeZone?: string): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const done = calendarDay(ms, timeZone);
  const today = calendarDay(now, timeZone);
  const back = today.day - done.day;
  const text =
    back === 0 || back === 1
      ? `${back === 0 ? "Today" : "Yesterday"} ${formatter("time", locale, timeZone).format(ms)}`
      : formatter(done.year === today.year ? "short" : "long", locale, timeZone).format(ms);
  // One token: the card's meta line may break before the date but never inside it ("Sep" / "22").
  return text.replace(/ /g, "\u00a0");
}
